/**
 * TanStack Query client + persistence wired for offline-tolerant writes.
 *
 * PROJECT.md §Principles — "Offline-tolerant writes from day one. Local-
 * first writes, sync queue, last-write-wins." That's what this does:
 *
 *  - Query cache is persisted to IndexedDB so reads survive reloads.
 *  - Mutations are persisted too, so a write made offline will replay
 *    automatically when the network returns (TanStack Query resumes
 *    paused mutations once `onlineManager` flips to online).
 *  - Each mutation has a `mutationKey` matched to a handler registered
 *    here, so replayed mutations after a refresh know what to do.
 *
 * Last-write-wins falls out naturally: optimistic updates patch the
 * cache immediately; the server's `updatedAt` is the eventual truth.
 */

import { QueryClient } from '@tanstack/react-query';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { get, set, del } from 'idb-keyval';
import {
  api,
  ApiError,
} from './api.js';
import type {
  BudgetSnapshot,
  DocumentCreateInput,
  DocumentUpdateInput,
  ObligationCreateInput,
  ObligationUpdateInput,
  PersonCreateInput,
  TripCreateInput,
  TripUpdateInput,
} from '@almanac/shared';

export const queryKeys = {
  obligations: ['obligations'] as const,
  documents: ['documents'] as const,
  lifeAreas: ['life-areas'] as const,
  people: ['people'] as const,
  me: ['me'] as const,
  budget: ['budget'] as const,
  trips: ['trips'] as const,
};

export const mutationKeys = {
  createObligation: ['obligation', 'create'] as const,
  updateObligation: ['obligation', 'update'] as const,
  completeObligation: ['obligation', 'complete'] as const,
  reopenObligation: ['obligation', 'reopen'] as const,
  deleteObligation: ['obligation', 'delete'] as const,
  createDocument: ['document', 'create'] as const,
  updateDocument: ['document', 'update'] as const,
  deleteDocument: ['document', 'delete'] as const,
  createPerson: ['person', 'create'] as const,
  saveBudgetMonth: ['budget', 'save'] as const,
  createTrip: ['trip', 'create'] as const,
  updateTrip: ['trip', 'update'] as const,
  deleteTrip: ['trip', 'delete'] as const,
};

export function createAppQueryClient(): QueryClient {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        // Long stale time + persistence = data is there when offline.
        staleTime: 1000 * 30,
        gcTime: 1000 * 60 * 60 * 24 * 7, // a week
        retry: (failureCount, error) => {
          // Auth failures don't recover by retrying — they recover by
          // re-running the navigation through Access. Surface them to
          // the UI immediately so the user gets a sign-in prompt.
          if (error instanceof ApiError && error.isAuth) return false;
          if (error instanceof Error && error.message.startsWith('HTTP 4')) return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: true,
      },
      mutations: {
        // Crucial: keep paused mutations across reloads. The persister
        // writes the cache+mutations to IndexedDB; on rehydration these
        // resume automatically once the browser is back online.
        retry: 3,
        networkMode: 'offlineFirst',
      },
    },
  });

  // Register mutation handlers by key. When PersistQueryClient rehydrates
  // a paused mutation it looks up the function via these keys.
  client.setMutationDefaults(mutationKeys.createObligation, {
    mutationFn: (input: ObligationCreateInput & { id: string }) => api.createObligation(input),
  });
  client.setMutationDefaults(mutationKeys.updateObligation, {
    mutationFn: (vars: { id: string; input: ObligationUpdateInput }) =>
      api.updateObligation(vars.id, vars.input),
  });
  client.setMutationDefaults(mutationKeys.completeObligation, {
    mutationFn: (vars: { id: string; completedAt?: string }) =>
      api.completeObligation(vars.id, vars.completedAt),
  });
  client.setMutationDefaults(mutationKeys.reopenObligation, {
    mutationFn: (vars: { id: string; nextDueDate?: string }) =>
      api.reopenObligation(vars.id, vars.nextDueDate),
  });
  client.setMutationDefaults(mutationKeys.deleteObligation, {
    mutationFn: (vars: { id: string }) => api.deleteObligation(vars.id),
  });
  client.setMutationDefaults(mutationKeys.createDocument, {
    mutationFn: (input: DocumentCreateInput & { id: string }) => api.createDocument(input),
  });
  client.setMutationDefaults(mutationKeys.updateDocument, {
    mutationFn: (vars: { id: string; input: DocumentUpdateInput }) =>
      api.updateDocument(vars.id, vars.input),
  });
  client.setMutationDefaults(mutationKeys.deleteDocument, {
    mutationFn: (vars: { id: string }) => api.deleteDocument(vars.id),
  });
  client.setMutationDefaults(mutationKeys.createPerson, {
    mutationFn: (input: PersonCreateInput & { id: string }) => api.createPerson(input),
  });
  client.setMutationDefaults(mutationKeys.saveBudgetMonth, {
    mutationFn: (vars: { month: string; data: BudgetSnapshot }) =>
      api.saveBudgetMonth(vars.month, vars.data),
  });
  client.setMutationDefaults(mutationKeys.createTrip, {
    mutationFn: (input: TripCreateInput & { id: string }) => api.createTrip(input),
  });
  client.setMutationDefaults(mutationKeys.updateTrip, {
    mutationFn: (vars: { id: string; input: TripUpdateInput }) =>
      api.updateTrip(vars.id, vars.input),
  });
  client.setMutationDefaults(mutationKeys.deleteTrip, {
    mutationFn: (vars: { id: string }) => api.deleteTrip(vars.id),
  });

  return client;
}

const STORAGE_KEY = 'almanac-tanstack-query';

export const queryPersister = createAsyncStoragePersister({
  storage: {
    getItem: (key: string) => get<string>(key).then((v) => v ?? null),
    setItem: (key: string, value: string) => set(key, value),
    removeItem: (key: string) => del(key),
  },
  key: STORAGE_KEY,
  throttleTime: 1000,
});
